import { HelpCircle, Search } from "lucide-react";
import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";
import SemanticSearchProgress from "@/components/SemanticSearchProgress";
import { Input } from "@/components/ui/input";
import { InfoTip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const SEMANTIC_SEARCH_TIP =
  "Kontekstowo szuka znaczenia tego, co wpiszesz (synonimy, podobne przeżycia), nie dosłownego słowa. " +
  "Przy niejednoznacznym haśle pisz pełniej, np. „kompleksy niższości, porównania, wstyd z wyglądu”. " +
  "Idiomy doprecyzuj sam („wszystko na mojej głowie = przeciążenie opieką”), system nic nie dopisuje. " +
  "Tryb Słowa = dokładne wystąpienie frazy w tekście.";

type Tab = "browse" | "stats" | "social-proof";

type Props = {
  tab: Tab;
  total?: number;
  totalWord?: string;
  q?: string;
  onQChange?: (v: string) => void;
  showSearch?: boolean;
  searchMode?: "keyword" | "semantic";
  onSearchModeChange?: (mode: "keyword" | "semantic") => void;
  searchHint?: string | null;
  searchPending?: boolean;
  indexedCount?: number | null;
  actions?: ReactNode;
  /** Panel poza główną kolumną (np. chat) - od góry, bierze resztę szerokości. */
  aside?: ReactNode;
  children: ReactNode;
};

export default function CommentsPageShell({
  tab,
  total,
  totalWord = "komentarzy",
  q = "",
  onQChange,
  showSearch = false,
  searchMode = "keyword",
  onSearchModeChange,
  searchHint,
  searchPending = false,
  indexedCount,
  actions,
  aside,
  children,
}: Props) {
  const showSemanticProgress =
    Boolean(searchPending && searchMode === "semantic" && (q || "").trim());

  const headerInner = (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Komentarze</h1>
        <p className="mt-1 max-w-xl text-sm text-slate-600">
          Głos spod rolek: IG i FB na żywo, YT i TT z dumpów. Narzędzia pod karuzele, nie skrzynka do
          odpowiedzi.
          <span
            className="ml-1 inline-flex align-middle text-slate-400"
            title="To komentarze publiczne pod rolkami, nie prywatne DM."
          >
            <HelpCircle className="h-3.5 w-3.5" />
          </span>
        </p>
        <div className="mt-4 inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
          <NavLink
            to="/komentarze"
            end
            className={({ isActive }) =>
              cn(
                "rounded-md px-3 py-1.5 text-sm font-medium",
                isActive || tab === "browse"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-800",
              )
            }
          >
            Przeglądarka
          </NavLink>
          <NavLink
            to="/komentarze/statystyki"
            className={({ isActive }) =>
              cn(
                "rounded-md px-3 py-1.5 text-sm font-medium",
                isActive || tab === "stats"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-800",
              )
            }
          >
            Statystyki
          </NavLink>
          <NavLink
            to="/komentarze/social-proof"
            className={({ isActive }) =>
              cn(
                "rounded-md px-3 py-1.5 text-sm font-medium",
                isActive || tab === "social-proof"
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-500 hover:text-slate-800",
              )
            }
          >
            Social proof
          </NavLink>
        </div>
      </div>
      <div className="flex w-full max-w-md flex-col items-stretch gap-3 sm:items-end">
        <div className="flex items-center justify-end gap-3">
          {actions}
          {total != null && !showSearch && (
            <div className="shrink-0 text-sm font-medium text-slate-600">
              {total} <span className="text-slate-400">{totalWord}</span>
            </div>
          )}
        </div>
        {showSearch && onQChange && (
          <div className="flex w-full flex-col gap-2">
            <div className="flex w-full items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  className="border-slate-200 bg-slate-50 pl-9 text-slate-900 placeholder:text-slate-400"
                  placeholder={
                    searchMode === "semantic"
                      ? "Szukaj kontekstowo (znaczenie, nie tylko słowa)…"
                      : "Szukaj w treści, autorze, tytule rolki…"
                  }
                  value={q}
                  onChange={(e) => onQChange(e.target.value)}
                />
              </div>
              {total != null && (
                <div className="shrink-0 text-sm font-medium text-slate-600">
                  {total} <span className="text-slate-400">{totalWord}</span>
                </div>
              )}
            </div>
            {onSearchModeChange && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => onSearchModeChange("keyword")}
                  className={cn(
                    "rounded-md border px-2.5 py-1 text-xs font-medium",
                    searchMode === "keyword"
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
                  )}
                >
                  Słowa
                </button>
                <button
                  type="button"
                  onClick={() => onSearchModeChange("semantic")}
                  className={cn(
                    "rounded-md border px-2.5 py-1 text-xs font-medium",
                    searchMode === "semantic"
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
                  )}
                >
                  Kontekstowo
                </button>
                <InfoTip text={SEMANTIC_SEARCH_TIP} className="text-slate-400 hover:text-slate-700" />
                {searchHint ? <span className="text-xs text-slate-500">{searchHint}</span> : null}
              </div>
            )}
            <SemanticSearchProgress
              active={showSemanticProgress}
              indexedCount={indexedCount}
              label="Szukam kontekstowo w komentarzach"
            />
          </div>
        )}
      </div>
    </div>
  );

  const header = (
    <header className="border-b border-slate-200 bg-white px-6 py-6 lg:px-8">
      {aside ? headerInner : <div className="mx-auto max-w-6xl">{headerInner}</div>}
    </header>
  );

  if (aside) {
    return (
      <div className={cn("comments-page -mx-8 -my-8 min-h-screen")}>
        <div className="flex min-h-screen flex-col lg:flex-row lg:items-stretch">
          <div className="min-w-0 flex-1 lg:flex-[2]">
            {header}
            <div className="px-6 py-6 lg:px-8">{children}</div>
          </div>
          <div className="flex w-full min-w-0 flex-col lg:sticky lg:-top-8 lg:-mt-8 lg:h-dvh lg:min-w-[18rem] lg:flex-1 lg:self-start lg:border-l lg:border-slate-200 lg:bg-white">
            {aside}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("comments-page -mx-8 -my-8 min-h-screen")}>
      {header}
      <div className="mx-auto max-w-6xl px-6 py-6 lg:px-8">{children}</div>
    </div>
  );
}
